// PublicNavigation.jsx - Version corrigée avec News en accès restreint
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Sparkles, Menu, X, Crown, Home, TrendingUp, BookOpen, 
  MessageCircle, Eye, Lock, ChevronLeft, ChevronRight
} from 'lucide-react';
import { useI18n } from '../CP-Multilingue/useI18n';

const PublicNavigation = ({ 
  onNavigate, 
  onShowAuth, 
  currentPath, 
  sidebarCollapsed = false, 
  onSidebarToggle 
}) => {
  const { t, isRTL } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  // Détecter la taille d'écran
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      const newIsMobile = width < 768;
      const newIsTablet = width >= 768 && width < 1024;
      
      setIsMobile(newIsMobile);
      setIsTablet(newIsTablet);

      if ((newIsMobile || newIsTablet) && onSidebarToggle) {
        onSidebarToggle(true);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, [onSidebarToggle]);

  // Navigation items avec traductions - News maintenant en accès restreint
  const navigationItems = [
    { 
      id: 'home', 
      label: t('navigation.home'), 
      path: '/', 
      icon: Home, 
      accessible: true, 
      section: t('navigation.sections.services') 
    },
    { 
      id: 'actualites', 
      label: t('navigation.news'), 
      path: null, // Changé de '/actualites' à null
      icon: TrendingUp, 
      accessible: false, // Changé de true à false
      premiumFeature: true, // Ajouté pour indiquer que c'est une fonctionnalité premium
      section: t('navigation.sections.information') 
    },
    { 
      id: 'learning', 
      label: t('navigation.learning'), 
      path: null, 
      icon: BookOpen, 
      accessible: false, 
      premiumFeature: true, 
      section: t('navigation.sections.information') 
    },
    { 
      id: 'chat', 
      label: t('navigation.chat'), 
      path: null, 
      icon: MessageCircle, 
      accessible: false, 
      premiumFeature: true, 
      section: t('navigation.sections.tools') 
    }
  ];

  const handleNavClick = (item) => {
    if (item.accessible && item.path) {
      navigate(item.path);
      onNavigate(item.id);
      if (isMobile || isTablet) onSidebarToggle(true);
    } else if (!item.accessible) {
      onShowAuth('register');
    }
  };

  const isCurrentPath = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const toggleSidebar = () => {
    if (onSidebarToggle) {
      onSidebarToggle(!sidebarCollapsed);
    }
  };

  const groupedItems = navigationItems.reduce((acc, item) => {
    if (!acc[item.section]) acc[item.section] = [];
    acc[item.section].push(item);
    return acc;
  }, {});

  const textAlign = isRTL ? 'text-right' : 'text-left';
  const flexDirection = isRTL ? 'flex-row-reverse' : 'flex-row';

  return (
    <>
      {/* Bouton hamburger - adapté RTL */}
      {(isMobile || isTablet) && (
        <button
          className={`fixed top-4 ${isRTL ? 'right-4' : 'left-4'} z-50 p-2 rounded-md bg-white shadow-lg`}
          onClick={() => onSidebarToggle(!sidebarCollapsed)}
        >
          {sidebarCollapsed ? (
            <Menu className="w-6 h-6 text-gray-700" />
          ) : (
            <X className="w-6 h-6 text-gray-700" />
          )}
        </button>
      )}

      {/* Overlay en mobile */}
      {(isMobile || isTablet) && !sidebarCollapsed && (
        <div
          className="fixed inset-0 bg-black/40 z-30"
          onClick={() => onSidebarToggle(true)}
        />
      )}

      {/* Sidebar - adapté RTL */}
      <div 
        className={`
          fixed top-0 ${isRTL ? 'right-0' : 'left-0'} h-screen bg-white shadow-xl z-40
          transition-transform duration-300 ease-in-out
          ${ (isMobile || isTablet)
              ? 'overflow-y-auto'
              : (sidebarCollapsed ? 'overflow-y-hidden' : 'overflow-y-auto')
          }
          ${ (isMobile || isTablet)
              ? (sidebarCollapsed ? `${isRTL ? 'translate-x-full' : '-translate-x-full'} w-80 max-w-[85vw]` : 'translate-x-0 w-80 max-w-[85vw]')
              : (sidebarCollapsed ? 'w-16 translate-x-0' : 'w-64 translate-x-0')
          }
        `}
      >
        {/* En-tête */}
        <div className={`h-16 border-b border-gray-200 flex items-center justify-between px-4 ${flexDirection}`}>
          <div className={`flex items-center ${sidebarCollapsed && !isMobile && !isTablet ? 'justify-center w-full' : ''} ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Sparkles className="w-8 h-8 text-purple-600 flex-shrink-0" /> 
            {(!sidebarCollapsed || isMobile || isTablet) && (
              <span className={`${isRTL ? 'mr-3' : 'ml-3'} font-bold text-xl text-purple-600`}>Aladinn</span>
            )}
          </div>
          
          {/* Collapse desktop uniquement */}
          {!isMobile && !isTablet && (
            <button
              onClick={toggleSidebar}
              className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
            >
              {sidebarCollapsed ? (
                isRTL ? <ChevronLeft className="w-5 h-5 text-gray-600" /> : <ChevronRight className="w-5 h-5 text-gray-600" />
              ) : (
                isRTL ? <ChevronRight className="w-5 h-5 text-gray-600" /> : <ChevronLeft className="w-5 h-5 text-gray-600" />
              )}
            </button>
          )}
        </div>

        <div className={`${(!isMobile && !isTablet && sidebarCollapsed)
          ? 'h-full overflow-hidden'
          : 'h-[calc(100vh-4rem)] overflow-y-auto'
        }`}>
          
          {/* Invitation inscription */}
          {(!sidebarCollapsed || isMobile || isTablet) && (
            <div className="p-4 border-b border-gray-200">
              <div className="text-center">
                <h3 className="font-semibold text-gray-900 mb-1">{t('navigation.visitor')}</h3>
                <p className="text-sm text-gray-600 mb-3">{t('navigation.discover')}</p>
                <button 
                  onClick={() => onShowAuth('register')}
                  className={`w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-md font-medium text-sm flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  <Crown className="w-4 h-4 mx-2" />
                  {t('navigation.registerFree')}
                </button>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="p-3">
            {Object.entries(groupedItems).map(([sectionName, items]) => (
              <div key={sectionName} className="mb-6">
                {(!sidebarCollapsed || isMobile || isTablet) && (
                  <h4 className={`text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 px-2 ${textAlign}`}>
                    {sectionName}
                  </h4>
                )}
                <div className="space-y-1">
                  {items.map((item) => {
                    const Icon = item.icon;
                    const isActive = item.path && isCurrentPath(item.path);
                    
                    return (
                      <div key={item.id} className="relative group">
                        <button
                          onClick={() => handleNavClick(item)}
                          className={`w-full flex items-center p-3 rounded-lg transition-colors ${
                            isActive 
                              ? 'bg-purple-50 text-purple-700' 
                              : item.accessible 
                              ? 'text-gray-600 hover:bg-purple-50 hover:text-purple-700' 
                              : 'text-gray-400 hover:text-gray-600'
                          } ${isRTL ? 'flex-row-reverse' : ''}`}
                          title={sidebarCollapsed && !isMobile && !isTablet ? item.label : ''}
                        >
                          <Icon className="w-5 h-5 mx-3 flex-shrink-0" />
                          {(!sidebarCollapsed || isMobile || isTablet) && (
                            <>
                              <span className={`font-medium truncate flex-grow ${textAlign}`}>{item.label}</span>
                              {item.badge && (
                                <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full flex-shrink-0">
                                  {item.badge}
                                </span>
                              )}
                              {item.premiumFeature && (
                                <Crown className="w-4 h-4 ml-2 text-amber-500 flex-shrink-0" />
                              )}
                            </>
                          )}
                        </button>
                        
                        {/* Tooltip pour sidebar collapsed */}
                        {sidebarCollapsed && !isMobile && !isTablet && (
                          <div className={`absolute ${isRTL ? 'right-full' : 'left-full'} top-1/2 transform -translate-y-1/2 ${isRTL ? 'mr-2' : 'ml-2'} opacity-0 group-hover:opacity-100 transition-opacity bg-gray-800 text-white text-xs px-3 py-2 rounded-lg whitespace-nowrap z-10 pointer-events-none`}>
                            <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <span>{item.label}</span>
                              {!item.accessible && <Lock className="w-3 h-3 mx-1" />}
                              {item.badge && <span className="ml-2 text-green-300">({item.badge})</span>}
                            </div>
                            <div className={`absolute ${isRTL ? 'left-full' : 'right-full'} top-1/2 transform -translate-y-1/2 w-2 h-2 bg-gray-800 rotate-45`}></div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Actions du bas */}
          {(!sidebarCollapsed || isMobile || isTablet) && (
            <div className={`absolute bottom-0 ${isRTL ? 'right-0' : 'left-0'} ${isRTL ? 'left-0' : 'right-0'} p-4 bg-white border-t border-gray-200`}>
              <div className="space-y-2">
                <button 
                  onClick={() => onShowAuth('login')}
                  className={`w-full flex items-center p-2 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  <Eye className="w-4 h-4 mx-3 flex-shrink-0" />
                  <span className={`text-sm truncate ${textAlign}`}>{t('navigation.login')}</span>
                </button>
                <button 
                  onClick={() => onShowAuth('register')}
                  className={`w-full flex items-center p-2 rounded-lg text-purple-600 hover:bg-purple-50 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  <Crown className="w-4 h-4 mx-3 flex-shrink-0" />
                  <span className={`text-sm truncate ${textAlign}`}>{t('navigation.register')}</span>
                </button>
              </div>
            </div>
          )}

          {/* Version collapsed desktop */}
          {sidebarCollapsed && !isMobile && !isTablet && (
            <div className={`absolute bottom-0 ${isRTL ? 'right-0 left-0' : 'left-0 right-0'} p-2 bg-white border-t border-gray-200`}>
              <div className="space-y-1">
                <button 
                  onClick={() => onShowAuth('login')}
                  className="w-full flex items-center justify-center p-2 rounded-lg text-gray-600 hover:bg-gray-50"
                  title={t('navigation.login')}
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => onShowAuth('register')}
                  className="w-full flex items-center justify-center p-2 rounded-lg text-purple-600 hover:bg-purple-50"
                  title={t('navigation.register')}
                >
                  <Crown className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PublicNavigation;